import { ChevronLeft } from "@/components/Icons/utility/ChevronLeft.tsx";
import { MobileCartBarProps } from "@/components/MobileCartBar/MobileCartBarProps.ts";
import { AvailabilityPopoverContent } from "@/components/Popover/AvailabilityPopoverContent.tsx";
import { useReservationSidebarData } from "@/components/ReservationSidebar/useReservationSidebarData.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { PaymentProviderEnum } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import { useCheckCartAccommodationAvailibility } from "@/pages/AccommodationPicking/hooks/useCheckCartAccommodationAvailibility.ts";
import { useIsFormValid } from "@/pages/BookingEngine/hooks/useIsFormValid.ts";
import { useMobileDrawer } from "@/pages/BookingEngine/hooks/useMobileDrawer.ts";
import { useValidateExtraOptionsStep } from "@/pages/ExtraOptions/hooks/useValidateExtraOptionsStep.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useValidFormStore } from "@/stores/ValidFormStore/ValidFormStore.ts";
import { ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "react-tiny-popover";

export const MobileCartBar = ({ holderFormData, paymentFormData }: MobileCartBarProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { containerEl } = useShadowRoot();
  const {
    cartAccommodationItems,
    calculateCartLoading,
    calculatedCartData,
    defaultDateRange,
    page,
    isCartItemInDisabledRateGroupFromRootAttributes,
    isForcedInquiry,
  } = useReservationSidebarData(!isMobile);
  const { paymentEnabled, paymentType } = useGetOrganization();
  const availibilityDisabled = useCheckCartAccommodationAvailibility();
  const { areParcelVehicleDimensionsMissing, areChildAgesMissing, isCaravanMoverMissing } =
    useValidateExtraOptionsStep();

  const shouldShowPaymentForm = useMemo(
    () => !isForcedInquiry && paymentEnabled && paymentType === PaymentProviderEnum.WsPayPer,
    [isForcedInquiry, paymentEnabled, paymentType],
  );

  const { isFullFormValid, isHolderFormValid } = useIsFormValid(holderFormData, paymentFormData);
  const { handleSubmit, reset: resetHolderForm } = holderFormData;
  const { handleSubmitPaymentForm, reset: resetPaymentForm, onSubmitPaymentForm } = paymentFormData;

  const {
    handleCloseCartDrawer,
    handleOpenCartDrawer,
    handleMobileContinue,
    handleMobileBack,
    getMobileBtnText,
  } = useMobileDrawer(handleSubmit, handleSubmitPaymentForm(onSubmitPaymentForm), () => {
    resetPaymentForm();
    resetHolderForm();
  });
  const isValid = useValidFormStore((state) => state.isValid);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const disableMobileContinue =
    // if no cartItems
    !cartAccommodationItems.length ||
    // if no dateRange
    !(defaultDateRange?.[0] && defaultDateRange?.[1]) ||
    (page === AppPage.ExtraOptions &&
      (areParcelVehicleDimensionsMissing || areChildAgesMissing || isCaravanMoverMissing)) ||
    // if on form page & invalid form
    (page === AppPage.HolderAndPaymentInfo &&
      (shouldShowPaymentForm ? !isFullFormValid : !isHolderFormValid)) ||
    availibilityDisabled ||
    !isValid ||
    isCartItemInDisabledRateGroupFromRootAttributes ||
    !!calculatedCartData?.error;

  let totalPrice: string | number = 0;
  if (calculatedCartData?.priceBreakdown?.withDiscount)
    totalPrice = formatCurrency(calculatedCartData.priceBreakdown?.withDiscount);
  if (calculatedCartData?.error) totalPrice = calculatedCartData?.error || t("error");

  if (page === AppPage.ReservationOverview) return null;

  return (
    <>
      <i className="o-booking-engine__mobile-cart-overlay" onClick={handleCloseCartDrawer}></i>
      <div
        className={`o-booking-engine__mobile-cart-bar ${cartAccommodationItems.length ? "is-visible" : ""}`}
      >
        {page !== AppPage.AccommodationPicking && (
          <button className="o-booking-engine__mobile-cart-bar-back" onClick={handleMobileBack}>
            <ChevronLeft />
          </button>
        )}
        <div className="o-booking-engine__mobile-cart-bar-price">
          <p className="o-booking-engine__mobile-cart-bar-price-label u-p1 u-fw-600">{t("total")}</p>
          <div
            className={`o-booking-engine__mobile-cart-bar-price-value u-h5 u-fw-600 ${
              calculatedCartData?.error ? "c-reservation-sidebar__total-error-price" : undefined
            }`}
          >
            <span className={calculateCartLoading ? "is-loading" : undefined}>{totalPrice}</span>
          </div>
        </div>
        {cartAccommodationItems.length > 0 && (
          <button className="o-booking-engine__mobile-cart-bar-cart-btn" onClick={handleOpenCartDrawer}>
            <ShoppingCart size={20} />
            <span className="o-booking-engine__mobile-cart-bar-cart-btn-badge">
              {cartAccommodationItems.length}
            </span>
          </button>
        )}
        <Popover isOpen={popoverOpen} parentElement={containerEl} content={AvailabilityPopoverContent}>
          <button
            className="c-be-button"
            onClick={handleMobileContinue}
            disabled={disableMobileContinue}
            onMouseOver={() => availibilityDisabled && setPopoverOpen(true)}
            onMouseLeave={() => availibilityDisabled && setPopoverOpen(false)}
          >
            <span className="c-be-button__label">{getMobileBtnText()}</span>
          </button>
        </Popover>
      </div>
    </>
  );
};
